import type { RecurrenceRule, Weekday } from '../db/models'
import { nthOfWeekday, weekday } from '../date/calendar'
import type { RecurrenceTone } from './recurrenceTone'
import { isValidEnd } from './end'

/** Die Einträge des Wiederkehr-Wählers — reine Voreinstellungen, kein gespeicherter Zustand. */
export type RecurrencePreset = 'daily' | 'weekly' | 'monthly' | 'monthlyWeekday' | 'yearly' | 'afterDone' | 'quota'

/** Mo–Fr. Die Seite **Wiederkehrend** beschriftet damit („Werktags"); eine Voreinstellung ist es nicht mehr. */
export const WORKDAYS: readonly Weekday[] = [0, 1, 2, 3, 4]

/**
 * **Die Gruppen des Auswahlfelds — nach Mechanismus.** Die drei tragen drei Töne, und die
 * Gruppierung ist, was davon im Auswahlfeld übrig bleibt (CONTEXT.md, Wiederkehr-Wähler).
 * „Nie" steht nicht hier: Es ist keine Voreinstellung, sondern das Fehlen einer Regel.
 */
export const RECURRENCE_PRESET_GROUPS: ReadonlyArray<{ tone: RecurrenceTone; presets: readonly RecurrencePreset[] }> = [
  { tone: 'fixed', presets: ['daily', 'weekly', 'monthly', 'monthlyWeekday', 'yearly'] },
  { tone: 'quota', presets: ['quota'] },
  { tone: 'after-done', presets: ['afterDone'] },
]

/**
 * Welcher Eintrag zur Regel gehört — abgeleitet, ohne dass irgendwo ein Preset gespeichert
 * wäre (CONTEXT.md, Wiederholung).
 *
 * **„Werktags (Mo–Fr)" gibt es seit 2026-09-19 nicht mehr.** Es war dieselbe Form wie
 * „Wöchentlich" mit fünf Pillen, und das Auswahlfeld sprang beim Antippen eines Wochentags
 * von „Werktags" auf „Wöchentlich" — ein Auswahlfeld, das springt, liest sich als „die App
 * hat meine Wahl geändert". Mo–Fr setzt man über die fünf Pillen.
 */
export function presetOf(rule: RecurrenceRule): RecurrencePreset {
  if (rule.type === 'after_done') return 'afterDone'
  if (rule.type === 'quota') return 'quota'
  switch (rule.unit) {
    case 'day': return 'daily'
    case 'week': return 'weekly'
    // „Jeden 3. Freitag“ — derselbe Monatsrhythmus, eigener Eintrag (Sven, 27.09.2026).
    case 'month': return rule.nthWeekday ? 'monthlyWeekday' : 'monthly'
    case 'year': return 'yearly'
  }
}

/** Die Startwerte eines Eintrags. `seed` ist das Startdatum der Aufgabe, sonst heute. */
export function ruleForPreset(preset: RecurrencePreset, seed: Date): RecurrenceRule {
  switch (preset) {
    case 'daily': return { type: 'fixed', interval: 1, unit: 'day' }
    case 'weekly': return { type: 'fixed', interval: 1, unit: 'week', weekdays: [weekday(seed)] }
    case 'monthly': return { type: 'fixed', interval: 1, unit: 'month', monthDay: seed.getDate() }
    case 'monthlyWeekday': return {
      type: 'fixed', interval: 1, unit: 'month', monthDay: seed.getDate(),
      nthWeekday: { nth: nthOfWeekday(seed), weekday: weekday(seed) },
    }
    case 'yearly': return { type: 'fixed', interval: 1, unit: 'year', yearMonth: seed.getMonth() + 1, yearDay: seed.getDate() }
    case 'afterDone': return { type: 'after_done', interval: 1, unit: 'day' }
    case 'quota': return { type: 'quota', count: 3, period: 'week' }
  }
}

const inRange = (n: number, min: number, max: number) => Number.isInteger(n) && n >= min && n <= max

/** Ob sich die Regel bestätigen lässt — der Wiederkehr-Wähler sperrt den Knopf sonst. */
export function isValidRule(rule: RecurrenceRule): boolean {
  if (!isValidEnd(rule.end)) return false
  switch (rule.type) {
    case 'quota': return inRange(rule.count, 1, 999)
    case 'after_done': return inRange(rule.interval, 1, 999)
    case 'fixed':
      if (!inRange(rule.interval, 1, 999)) return false
      switch (rule.unit) {
        case 'day': return true
        case 'week': return rule.weekdays.length > 0 && rule.weekdays.every(d => inRange(d, 0, 6))
        case 'month': return inRange(rule.monthDay, 1, 31)
          && (!rule.nthWeekday || ([1, 2, 3, 4, -1].includes(rule.nthWeekday.nth) && inRange(rule.nthWeekday.weekday, 0, 6)))
        case 'year': return inRange(rule.yearMonth, 1, 12) && inRange(rule.yearDay, 1, 31)
      }
  }
}
